"use client";

import { motion } from "framer-motion";
import { Award, Trophy, Star, Shield, CheckCircle, Users, Clock } from "lucide-react";
import { fadeInUp, staggerContainer } from "@/utils/animations";
import { useLanguage } from "@/contexts/LanguageContext";

// Icon mapping for About component
const aboutIconMap = {
  "Award": Award,
  "Shield": Shield,
  "Users": Users,
  "Clock": Clock,
  "Trophy": Trophy,
  "CheckCircle": CheckCircle,
  "Star": Star,
};

const About = () => {
  const { t, getQualityIndicators } = useLanguage();
  const qualityIndicators = getQualityIndicators();

  return (
    <section id="about" className="py-20 md:py-28 bg-background theme-transition" aria-labelledby="about-title">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Elegant Banner Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="mb-16 md:mb-24 text-center"
        >
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/10 blur-xl rounded-full" />
              <div className="relative bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 p-4 rounded-2xl">
                <Trophy className="w-8 h-8 md:w-10 md:h-10 text-primary" aria-hidden="true" />
              </div>
            </div>
          </div>
          
          <h2 id="about-title" className="font-inter font-bold text-3xl sm:text-4xl md:text-5xl text-foreground mb-6">
            {t('awards.title')}
          </h2>
          <p className="font-inter font-normal text-base md:text-lg text-foreground/80 max-w-2xl mx-auto">
            {t('awards.description')}
          </p>
        </motion.div>

        {/* Sleek Quality Indicators */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" aria-label="Quality indicators and professional recognition">
            {qualityIndicators.map((indicator, index) => {
              const Icon = aboutIconMap[indicator.iconName as keyof typeof aboutIconMap] || Award;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  transition={{ delay: indicator.delay }}
                  className="group relative"
                >
                  <div className="relative h-full bg-card border border-border rounded-xl p-6 md:p-8 hover:border-primary/30 transition-all duration-300 theme-transition hover:shadow-lg hover:shadow-primary/5">
                    {/* Background Glow Effect */}
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
                    
                    {/* Icon */}
                    <div className="relative z-10 mb-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg bg-gradient-to-br from-background to-card border border-border group-hover:border-primary/50 transition-colors duration-300">
                        <Icon className={`w-6 h-6 md:w-7 md:h-7 ${indicator.color} transition-transform duration-300 group-hover:scale-110`} aria-hidden="true" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h3 className="font-inter font-bold text-lg md:text-xl text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                        {indicator.title}
                      </h3>
                      <p className="font-inter font-normal text-sm text-foreground/80 leading-relaxed">
                        {indicator.description}
                      </p>
                    </div>

                    {/* Decorative Line */}
                    <div className="relative z-10 mt-6 pt-6 border-t border-border">
                      <div className="flex items-center justify-between">
                        <span className="font-inter font-medium text-sm text-foreground/80">
                          {t('awards.excellenceIndicator')}
                        </span>
                        <div className="flex items-center">
                          {[...Array(3)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-3 h-3 text-primary fill-primary mx-0.5"
                              aria-hidden="true"
                            />
                          ))}
                          <span className="sr-only">Three star rating</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Elegant Divider */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-16 md:mt-24"
        >
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" aria-hidden="true" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-6 font-inter font-medium text-sm text-primary">
                {t('awards.professionalRecognition')}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Minimal Statistics */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-12 md:mt-16"
          aria-label="Professional statistics"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center mb-4">
                <div className="absolute w-16 h-16 bg-primary/10 rounded-full" aria-hidden="true" />
                <div className="relative font-inter font-bold text-3xl md:text-4xl text-primary">
                  50+
                </div>
              </div>
              <div className="font-inter font-medium text-sm text-foreground">
                {t('awards.successfulProjects')}
              </div>
            </div>
            
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center mb-4">
                <div className="absolute w-16 h-16 bg-primary/10 rounded-full" aria-hidden="true" />
                <div className="relative font-inter font-bold text-3xl md:text-4xl text-primary">
                  100%
                </div>
              </div>
              <div className="font-inter font-medium text-sm text-foreground">
                {t('awards.clientSatisfaction')}
              </div>
            </div>
            
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center mb-4">
                <div className="absolute w-16 h-16 bg-primary/10 rounded-full" aria-hidden="true" />
                <div className="relative font-inter font-bold text-3xl md:text-4xl text-primary">
                  8+
              </div>
              </div>
              <div className="font-inter font-medium text-sm text-foreground">
                {t('awards.yearsExperience')}
              </div>
            </div>
            
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center mb-4">
                <div className="absolute w-16 h-16 bg-primary/10 rounded-full" aria-hidden="true" />
                <div className="relative font-inter font-bold text-3xl md:text-4xl text-primary">
                  ∞
                </div>
              </div>
              <div className="font-inter font-medium text-sm text-foreground">
                {t('awards.qualityCommitment')}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Closing Statement */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-16 md:mt-24 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center mb-6">
              <CheckCircle className="w-6 h-6 text-primary mr-3" aria-hidden="true" />
              <span className="font-inter font-medium text-sm text-primary uppercase tracking-wider">
                {t('awards.professionalStandards')}
              </span>
            </div>
            <p className="font-inter font-normal text-base md:text-lg text-foreground/80 leading-relaxed">
              {t('awards.closingStatement')}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;